import { environment } from '../config/environment';
import { useApiClient } from './api'; // Assuming you have your api client here
import { TaxConfig, TaxPersonalData, TaxSettings, UserDetails, UserNotification } from './user.types';

export const useUserService = () => {
    const apiClient = useApiClient();

    const saveDetails = async (userDetails: UserDetails) => {
        try {
            await apiClient.post(`${environment.ctLiteApiUrl}/user/details`, userDetails);
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getSettings = async () => {
        try {
            const response = await apiClient.get(`${environment.ctLiteApiUrl}/user/settings`);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const saveSettings = async (settings: any) => {
        try {
            await apiClient.post(`${environment.ctLiteApiUrl}/user/settings`, settings, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getTaxSettingsConfig = async () => {
        try {
            const response = await apiClient.get<TaxConfig[]>(`${environment.ctLiteApiUrl}/user/tax-settings-config`);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getTaxSettings = async () => {
        try {
            const response = await apiClient.get<TaxSettings>(`${environment.ctLiteApiUrl}/user/tax-settings`);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const saveTaxSettings = async (taxSettings: TaxSettings) => {
        try {
            await apiClient.post(`${environment.ctLiteApiUrl}/user/tax-settings`, taxSettings);
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getTaxPersonalData = async () => {
        try {
            const response = await apiClient.get<TaxPersonalData>(`${environment.ctLiteApiUrl}/user/tax-personal-data`);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const saveTaxPersonalData = async (personalData: TaxPersonalData) => {
        try {
            await apiClient.post(`${environment.ctLiteApiUrl}/user/tax-personal-data`, personalData);
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getHistoricCoinValueInUserCurrency = async (coin: string, time: number, value: number) => {
        try {
            const params = new URLSearchParams();
            params.set('coin', coin);
            params.set('time', String(time));
            params.set('value', String(value));

            const response = await apiClient.get(
                `${environment.ctLiteApiUrl}/user/historical-coin-value?${params.toString()}`
            );
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getNotifications = async (count: number): Promise<UserNotification[]> => {
        try {
            const response = await apiClient.get<UserNotification[]>(
                `${environment.ctLiteApiUrl}/user/notifications?count=${count}`
            );
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const notificationsMarkAsRead = async () => {
        try {
            const response = await apiClient.post(`${environment.ctLiteApiUrl}/user/notifications/mark-as-read`);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const deleteAccount = async () => {
        try {
            await apiClient.post(`${environment.ctLiteApiUrl}/user/delete`);
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const finihsOnboarding = async () => {
        try {
            await apiClient.post(`${environment.ctLiteApiUrl}/user/finish-onboarding`);
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const log = async (action: string, details: string | null | undefined = undefined) => {
        try {
            const params = new URLSearchParams();
            params.set('action', action);
            if (details) params.set('details', String(details));
            await apiClient.post(`${environment.ctLiteApiUrl}/user/log?${params.toString()}`);
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getIdToken = async (code: string) => {
        try {
            const response = await apiClient.get<string>(`${environment.expressApiUrl}/api/user/get-id-token?code=${code}`);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const generateEmailSignature = async (email: string) => {
        try {
            const response = await apiClient.post<{signature: string}>(`${environment.expressApiUrl}/api/user/generate-email-signature`, { email });
            return response.data.signature;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    return {
        saveDetails,
        getSettings,
        saveSettings,
        getTaxSettingsConfig,
        getTaxSettings,
        saveTaxSettings,
        getTaxPersonalData,
        saveTaxPersonalData,
        getHistoricCoinValueInUserCurrency,
        getNotifications,
        notificationsMarkAsRead,
        deleteAccount,
        finihsOnboarding,
        log,
        getIdToken,
        generateEmailSignature,
    };
};
